import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { Card, Stack, Title, Text, Group, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * Who has opened this tenant's console as the tenant, and when.
 *
 * Impersonation is the most consequential action staff can take, and until now
 * the only record of it lived in the database. `GET /v1/admin/impersonations`
 * has always returned the last hundred mints; nothing in the console read it.
 */
export function ImpersonationsCard({ tenantId }: { tenantId: string }) {
  const t = useT();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'impersonations'],
    queryFn: () => admin.listImpersonations()
  });

  const rows = useMemo(
    () => (data?.data ?? []).filter((row) => row.tenant_id === tenantId).slice(0, 8),
    [data, tenantId]
  );

  return (
    <Card withBorder>
      <Stack gap="md">
        <Stack gap="sm">
          <Title order={4}>{t('tenants.view.impersonations.title')}</Title>
          <Text fz="xs" c="var(--rr-color-text-muted)">{t('tenants.view.impersonations.description')}</Text>
        </Stack>
        {error ? (
          <Alert color="error">{t('tenants.view.impersonations.loadError')}</Alert>
        ) : isLoading ? (
          <Stack gap="sm"><Skeleton height={18} /><Skeleton height={18} /></Stack>
        ) : rows.length === 0 ? (
          <Text fz="sm" c="var(--rr-color-text-muted)">{t('tenants.view.impersonations.empty')}</Text>
        ) : (
          <Stack gap="sm">
            {rows.map((row) => {
              const live = row.expires_at ? new Date(row.expires_at).getTime() > Date.now() : false;
              return (
                <Group key={row.id} gap="sm" align="baseline" wrap="nowrap">
                  <StatusBadge size="xs" status={live ? 'warning' : 'inactive'}>
                    {live ? t('tenants.view.impersonations.live') : t('status.expired')}
                  </StatusBadge>
                  <Text fz="sm" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.admin_email ?? t('common.unknown')}
                  </Text>
                  <span title={formatDateTime(row.created_at)} style={{ whiteSpace: 'nowrap' }}>
                    <Text fz="xs" c="var(--rr-color-text-muted)" span>
                      {formatRelative(row.created_at)}
                    </Text>
                  </span>
                </Group>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
